import { useEffect, useRef } from "react";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/components/ui/theme-provider";
import { useQuery } from "@tanstack/react-query";
import { Network, Globe } from "lucide-react";

interface NetworkNode {
  id: string;
  x: number;
  y: number;
  label: string;
  color: string;
  size: number;
}

export function NetworkVisualization() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { theme } = useTheme();

  // Fetch real scan data for network dependencies
  const { data: scans } = useQuery({
    queryKey: ["/api/scans"],
    refetchInterval: 30000 // Update every 30 seconds
  });

  // Get the latest completed scan to extract real network data (prefer successful scans over failed ones)
  const latestScan = scans?.filter(scan => scan.status === 'completed' && scan.results)?.[0];
  let scanResults = null;
  let centralDomain = "No Active Scan";
  let externalDomains: string[] = [];
  let scanStatus = "--";
  let securityScore = 0;

  if (latestScan) {
    try {
      scanResults = JSON.parse(latestScan.results);
      centralDomain = new URL(latestScan.url).hostname;
      
      // Extract real security score from scan results
      // Prioritize complianceScore (uses improved 7-component weighted average)
      if (scanResults.complianceScore !== undefined && scanResults.complianceScore !== null) {
        securityScore = scanResults.complianceScore;
        scanStatus = `${securityScore}%`;
      } else if (scanResults.securityScore !== undefined && scanResults.securityScore !== null) {
        securityScore = scanResults.securityScore;
        scanStatus = `${securityScore}%`;
      } else {
        // Fallback: calculate score from available data if complianceScore is missing
        // This ensures we always show a score when scan data is available
        const scripts = scanResults.scripts || [];
        const vulnerabilities = scanResults.vulnerabilities || [];
        const thirdPartyDomains = scanResults.thirdPartyDomains || [];
        const cookies = scanResults.cookies || [];
        const securityHeaders = scanResults.securityHeaders || {};
        
        if (scripts.length > 0 || vulnerabilities.length > 0 || thirdPartyDomains.length > 0) {
          // Simple fallback calculation (basic penalty-based)
          let fallbackScore = 100;
          fallbackScore -= vulnerabilities.length * 10;
          fallbackScore -= thirdPartyDomains.length * 2;
          const dangerousScripts = scripts.filter((s: any) => s.containsEval || s.containsFunction).length;
          fallbackScore -= dangerousScripts * 5;
          securityScore = Math.max(20, Math.min(100, fallbackScore));
          scanStatus = `${securityScore}%`;
        } else {
          scanStatus = "Completed";
        }
      }
      
      // Extract third-party domains from scan results
      if (scanResults.thirdPartyDomains) {
        externalDomains = scanResults.thirdPartyDomains.slice(0, 8); // Limit to 8 for visualization
      } else if (scanResults.scripts) {
        // Extract domains from script sources
        const domains = new Set<string>();
        scanResults.scripts.forEach((script: any) => {
          if (script.src && script.src !== 'inline') {
            try {
              const domain = new URL(script.src).hostname;
              if (domain !== centralDomain) {
                domains.add(domain);
              }
            } catch (e) {
              // Skip invalid URLs
            }
          }
        });
        externalDomains = Array.from(domains).slice(0, 8);
      }
    } catch (e) {
      console.error('Error parsing scan results:', e);
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size to match container
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const width = rect.width;
    const height = rect.height;
    const isDark = theme === "dark" || (theme === "system" && window.matchMedia('(prefers-color-scheme: dark)').matches);

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Create nodes from real scan data
    const colors = ["#3b82f6", "#ef4444", "#10b981", "#8b5cf6", "#f59e0b", "#06b6d4", "#84cc16", "#ec4899"];
    const positions = [
      { x: width * 0.75, y: height * 0.2 },
      { x: width * 0.8, y: height * 0.45 },
      { x: width * 0.75, y: height * 0.7 },
      { x: width * 0.85, y: height * 0.35 },
      { x: width * 0.85, y: height * 0.65 },
      { x: width * 0.9, y: height * 0.8 },
      { x: width * 0.25, y: height * 0.3 },
      { x: width * 0.2, y: height * 0.6 }
    ];

    const nodes: NetworkNode[] = [
      { id: "center", x: width / 2, y: height / 2, label: centralDomain, color: "#6366f1", size: 10 }
    ];

    // Add external domain nodes if we have real data
    if (externalDomains.length > 0) {
      externalDomains.forEach((domain, index) => {
        if (index < positions.length) {
          nodes.push({
            id: `external-${index}`,
            x: positions[index].x,
            y: positions[index].y,
            label: domain,
            color: colors[index % colors.length],
            size: 7
          });
        }
      });
    } else if (latestScan) {
      // If no external domains, show a message node
      nodes.push({
        id: "no-external",
        x: width * 0.75,
        y: height * 0.4,
        label: "No external dependencies",
        color: "#10b981",
        size: 6
      });
    }

    // Draw connections with animation effect
    ctx.strokeStyle = isDark ? "#64748b" : "#cbd5e1";
    ctx.lineWidth = 2;
    ctx.setLineDash([]);

    // Draw connections
    nodes.slice(1).forEach((node, index) => {
      ctx.beginPath();
      ctx.moveTo(nodes[0].x, nodes[0].y);
      ctx.lineTo(node.x, node.y);
      
      // Gradient for connections
      const gradient = ctx.createLinearGradient(nodes[0].x, nodes[0].y, node.x, node.y);
      gradient.addColorStop(0, nodes[0].color);
      gradient.addColorStop(1, node.color);
      ctx.strokeStyle = gradient;
      ctx.globalAlpha = 0.6;
      ctx.stroke();
      ctx.globalAlpha = 1;
    });

    // Draw nodes
    nodes.forEach(node => {
      // Node shadow
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 4;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
      
      ctx.fillStyle = node.color;
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.size, 0, 2 * Math.PI);
      ctx.fill();
      
      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Add white border
      ctx.strokeStyle = isDark ? "#1e293b" : "#ffffff";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Add labels
      if (node.id !== "center") {
        ctx.fillStyle = isDark ? "#e2e8f0" : "#475569";
        ctx.font = "11px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
        ctx.textAlign = node.x < width / 2 ? "right" : "left";
        const labelX = node.x + (node.x < width / 2 ? -node.size - 8 : node.size + 8);
        ctx.fillText(node.label, labelX, node.y + 4);
      }
    });

    // Draw center node with special styling
    const centerNode = nodes[0];
    ctx.fillStyle = isDark ? "#ffffff" : "#000000";
    ctx.font = "bold 13px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(centerNode.label, centerNode.x, centerNode.y - centerNode.size - 8);

    // Add legend with detailed third-party domain analysis
    const legendY = height - 30;
    ctx.font = "10px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    ctx.fillStyle = isDark ? "#94a3b8" : "#64748b";
    ctx.textAlign = "left";
    
    if (latestScan) {
      const legendText = `Live scan data from ${centralDomain} - ${externalDomains.length} external domains detected`;
      ctx.fillText(legendText, 10, legendY);
      
      // Add security score info if available
      if (securityScore > 0) {
        ctx.fillText(`Security Score: ${securityScore}%`, 10, legendY + 12);
      }
    } else {
      ctx.fillText("No active scan data", 10, legendY);
    }

  }, [theme, scans, centralDomain, externalDomains.length, scanStatus, securityScore]);

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <Link href="/network-analysis">
            <CardTitle className="flex items-center gap-2 cursor-pointer hover:text-emerald-700 transition-colors">
              <Network className="h-5 w-5 text-purple-600" />
              Network Dependencies
            </CardTitle>
          </Link>
          <div className="flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400">
            <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
            <span>Live</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative h-80 overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
          <canvas
            ref={canvasRef}
            className="w-full h-full"
            style={{ width: '100%', height: '100%' }}
          />
        </div>
        
        {/* Network Stats with Real Third-Party Domain Analysis */}
        <div className="mt-4 grid grid-cols-3 gap-4 text-center">
          <Link href="/network-analysis">
            <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
              <div className="text-sm font-medium text-slate-900 dark:text-white">{externalDomains.length}</div>
              <div className="text-xs text-slate-600 dark:text-slate-400">Third-Party Domains</div>
            </div>
          </Link>
          <Link href="/network-analysis">
            <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
              <div className="text-sm font-medium text-slate-900 dark:text-white">{externalDomains.length + 1}</div>
              <div className="text-xs text-slate-600 dark:text-slate-400">Network Dependencies</div>
            </div>
          </Link>
          <Link href="/scripts">
            <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
              <div className="text-sm font-medium text-purple-600">{scanStatus}</div>
              <div className="text-xs text-slate-600 dark:text-slate-400">Security Score</div>
            </div>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
